import { forwardRef, useImperativeHandle, useState } from 'react'
import {
  Thead,
  Tr,
  Th,
  Checkbox,
  HStack,
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Text,
} from '@chakra-ui/react'
import { ArrowUpDown, ArrowUp, ArrowDown, Filter, X } from 'lucide-react'
import type { DataField } from './types'
import { useThemeColors } from '@/hooks/useThemeColors'

interface TableHeaderProps {
  header: Record<string, DataField>
  onCheckAll?: (checked: boolean) => void
  onSort?: (field: string, sort: 'asc' | 'desc') => void
  onFilter?: (field: string, value: string | number | null) => void
  activeFilters?: Record<string, string | number>
  showActionsColumn?: boolean
  hideSelection?: boolean
}

export interface TableHeaderRef {
  setCheckedAll: (checked: boolean) => void
}

export const TableHeader = forwardRef<TableHeaderRef, TableHeaderProps>(
  ({ header, onCheckAll, onSort, onFilter, activeFilters = {}, showActionsColumn, hideSelection }, ref) => {
    const colors = useThemeColors()
    const [isCheckedAll, setIsCheckedAll] = useState(false)
    const [sortState, setSortState] = useState<Record<string, 'asc' | 'desc'>>({})
    const [openFilterMenu, setOpenFilterMenu] = useState<string | null>(null)

    const handleCheckAll = (checked: boolean) => {
      setIsCheckedAll(checked)
      onCheckAll?.(checked)
    }

    useImperativeHandle(ref, () => ({
      setCheckedAll: setIsCheckedAll,
    }))

    const handleSort = (field: string) => {
      const currentSort = sortState[field] || 'asc'
      const newSort = currentSort === 'asc' ? 'desc' : 'asc'

      setSortState((prev) => ({
        ...prev,
        [field]: newSort,
      }))

      onSort?.(field, newSort)
    }

    const sortIconColor = colors.textMuted
    const hoverBg = colors.sectionBg
    const theadBg = colors.cardBg
    const menuBg = colors.cardBg

    const handleFilterClick = (e: React.MouseEvent, field: string) => {
      e.stopPropagation()
      setOpenFilterMenu(openFilterMenu === field ? null : field)
    }

    const handleFilterSelect = (field: string, value: string | number | null) => {
      onFilter?.(field, value)
      setOpenFilterMenu(null)
    }

    const getSortIcon = (field: string) => {
      const sort = sortState[field]
      if (!sort) return <ArrowUpDown size={14} style={{ color: sortIconColor }} />
      return sort === 'asc' ? <ArrowUp size={14} style={{ color: sortIconColor }} /> : <ArrowDown size={14} style={{ color: sortIconColor }} />
    }

    const renderFilterIcon = (field: string, hasFilter: boolean) => {
      if (!hasFilter) return null
      const isActive = activeFilters[field] !== undefined && activeFilters[field] !== null

      return (
        <Menu isOpen={openFilterMenu === field} onClose={() => setOpenFilterMenu(null)} strategy="fixed">
          <MenuButton
            as={IconButton}
            aria-label="Filter"
            icon={<Filter size={14} />}
            size="xs"
            variant="ghost"
            colorScheme={isActive ? 'blue' : 'gray'}
            onClick={(e) => handleFilterClick(e, field)}
            ml={1}
          />
          <MenuList bg={menuBg} minW="200px" zIndex={1000}>
            <MenuItem onClick={() => handleFilterSelect(field, null)}>
              <HStack spacing={2} w="100%">
                <Text flex={1}>All</Text>
                {isActive && <X size={12} />}
              </HStack>
            </MenuItem>
            {header[field].filterOptions?.map((option) => (
              <MenuItem
                key={option.value}
                onClick={() => handleFilterSelect(field, option.value)}
                bg={activeFilters[field] === option.value ? 'blue.50' : 'transparent'}
              >
                {option.label}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      )
    }

    return (
      <Thead bg={theadBg}>
        <Tr bg={theadBg}>
          {!hideSelection && (
            <Th width="50px" bg={theadBg} py={4} px={4}>
              <Checkbox isChecked={isCheckedAll} onChange={(e) => handleCheckAll(e.target.checked)} colorScheme="blue" />
            </Th>
          )}
          {Object.keys(header).map((key) => (
            <Th
              key={key}
              whiteSpace="nowrap"
              bg={theadBg}
              py={4}
              px={4}
              cursor={header[key].withSort ? 'pointer' : 'default'}
              onClick={() => header[key].withSort && handleSort(key)}
              _hover={header[key].withSort ? { bg: hoverBg } : {}}
              position="relative"
            >
              <HStack spacing={2} justify="space-between">
                <HStack spacing={2}>
                  <span>{header[key].name}</span>
                  {header[key].withSort && (
                    <Box as="span" display="inline-flex" alignItems="center">
                      {getSortIcon(key)}
                    </Box>
                  )}
                </HStack>
                {renderFilterIcon(key, !!header[key].withFilter)}
              </HStack>
            </Th>
          ))}
          {showActionsColumn && (
            <Th width="80px" textAlign="right" bg={theadBg} py={4} px={4}>
              Actions
            </Th>
          )}
        </Tr>
      </Thead>
    )
  }
)

TableHeader.displayName = 'TableHeader'


